import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiTrendingUp } from 'react-icons/fi';
import PostCard from '../../components/PostCard/PostCard';
import PostListView from '../../components/PostListView/PostListView';
import PostGalleryView from '../../components/PostGalleryView/PostGalleryView';
import VideoCard from '../../components/VideoCard/VideoCard';
import VideoReelCard from '../../components/VideoReelCard/VideoReelCard';
import MemeCard from '../../components/MemeCard/MemeCard';
import FilterBar from '../../components/FilterBar/FilterBar';
import ViewToggle from '../../components/ViewToggle/ViewToggle';
import { postsAPI } from '../../services/api';
import { CATEGORIES } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import './Home.css';

const Home = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [featuredPost, setFeaturedPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [trendingPosts, setTrendingPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState('foryou');
  const [activeView, setActiveView] = useState('card');
  const [activeCategory, setActiveCategory] = useState(null);
  const observerRef = useRef();
  const loadMoreRef = useRef();

  useEffect(() => {
    setPage(1);
    setRecentPosts([]);
    setHasMore(true);
    fetchPosts(1, true);
  }, [activeFilter, activeCategory]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMorePosts();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, loadingMore, hasMore, page]);

  const loadMorePosts = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchPosts(nextPage, false);
  }, [page, loadingMore, hasMore, activeFilter]);

  const fetchPosts = async (pageNum = 1, isNewFilter = false) => {
    try {
      if (isNewFilter) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const limit = 12;
      const category = activeCategory || undefined;
      
      // Determinar qué tipo de posts cargar según el filtro
      if (activeFilter === 'following') {
        // Avoid triggering global 401 redirect when user is not logged in
        if (!isAuthenticated) {
          if (isNewFilter) {
            setRecentPosts([]);
            setFeaturedPost(null);
            setTrendingPosts([]);
          }
          setHasMore(false);
          return;
        }

        const response = await postsAPI.getFollowing({ limit, page: pageNum, category });
        const posts = response.data.posts || [];

        if (isNewFilter) {
          setRecentPosts(posts);
          setFeaturedPost(posts.find(p => p.type === 'article') || null);
          setTrendingPosts([]);
        } else {
          setRecentPosts(prev => [...prev, ...posts]);
        }
        setHasMore(posts.length >= limit);
      } else if (activeFilter === 'videos') {
        const [horizontalRes, reelsRes] = await Promise.all([
          postsAPI.getAll({ type: 'video-horizontal', limit, page: pageNum, category }),
          postsAPI.getAll({ type: 'video-reel', limit, page: pageNum, category })
        ]);
        const allVideos = [...horizontalRes.data.posts, ...reelsRes.data.posts];
        
        if (isNewFilter) {
          setRecentPosts(allVideos);
          setFeaturedPost(allVideos[0]);
        } else {
          setRecentPosts(prev => [...prev, ...allVideos]);
        }
        setHasMore(allVideos.length >= limit);
      } else if (activeFilter === 'memes') {
        const response = await postsAPI.getAll({ type: 'meme', limit, page: pageNum, category });
        const posts = response.data.posts || [];
        
        if (isNewFilter) {
          setRecentPosts(posts);
          setFeaturedPost(posts[0]);
        } else {
          setRecentPosts(prev => [...prev, ...posts]);
        }
        setHasMore(posts.length >= limit);
      } else {
        const response = await postsAPI.getAll({ type: 'article', limit, page: pageNum, category });
        const posts = response.data.posts || [];
        
        if (isNewFilter) {
          setRecentPosts(posts);
          setFeaturedPost(posts[0]);
          const trending = [...posts].sort((a, b) => b.likes - a.likes).slice(0, 6);
          setTrendingPosts(trending);
        } else {
          setRecentPosts(prev => [...prev, ...posts]);
        }
        setHasMore(posts.length >= limit);
      }
      
    } catch (error) {
      console.error('Error fetching posts:', error);
      if (isNewFilter) {
        setRecentPosts([]);
        setFeaturedPost(null);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleViewChange = (view) => {
    setActiveView(view);
  };

  const handleCategoryToggle = (categoryId) => {
    setActiveCategory(prev => (prev === categoryId ? null : categoryId));
  };

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const safePostSlug = (p) => {
    if (p?.slug) return p.slug;
    const base = slugify(p?.title) || 'post';
    return p?.id ? `${base}-${p.id}` : base;
  };

  const postRoute = (p) => {
    const slug = safePostSlug(p);
    if (p?.type === 'meme') return `/meme/${slug}`;
    if (p?.type === 'video-horizontal' || p?.type === 'video-reel') return `/video/${slug}`;
    return `/article/${slug}`;
  };

  const renderPost = (post) => {
    // List/Gallery should apply to ALL types
    if (activeView === 'list') {
      return <PostListView key={post.id} post={post} />;
    }
    if (activeView === 'gallery') {
      return <PostGalleryView key={post.id} post={post} />;
    }

    // Card view - render according to content type
    if (post.type === 'video-horizontal') {
      return <VideoCard key={post.id} post={post} />;
    }
    if (post.type === 'video-reel') {
      return <VideoReelCard key={post.id} post={post} isVisible={true} />;
    }
    if (post.type === 'meme') {
      return <MemeCard key={post.id} post={post} />;
    }
    return <PostCard key={post.id} post={post} />;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <FilterBar activeFilter={activeFilter} onFilterChange={handleFilterChange} />
      
      <div className="feed-container">
        {/* Main Feed */}
        <main className="main-feed">
          {/* View Toggle */}
          <div className="feed-controls">
            <ViewToggle activeView={activeView} onViewChange={handleViewChange} />
          </div>

          {/* Featured Post - Only in card view and only for articles */}
          {featuredPost && featuredPost.type === 'article' && activeView === 'card' && activeFilter !== 'videos' && activeFilter !== 'memes' && (
            <div className="featured-post">
              <PostCard post={featuredPost} variant="featured" />
            </div>
          )}

          {/* Posts Feed */}
          <div className={`posts-feed ${
            activeView === 'card' && activeFilter === 'memes' ? 'posts-feed-memes' : 
            activeView === 'card' && activeFilter === 'videos' ? 'posts-feed-videos' : 
            `posts-feed-${activeView}`
          }`}>
            {activeFilter === 'following' && !isAuthenticated && (
              <div style={{ padding: 16 }}>
                <p>
                  💡 <Link to="/login">Inicia sesión</Link> para ver el feed de Seguidos.
                </p>
              </div>
            )}
            {recentPosts.map(post => renderPost(post))}
          </div>

          {/* Infinite Scroll Trigger */}
          <div ref={loadMoreRef} className="infinite-scroll-trigger">
            {loadingMore && (
              <div className="loading-more">
                <div className="spinner-small"></div>
                <span>Cargando más...</span>
              </div>
            )}
            {!hasMore && recentPosts.length > 0 && (
              <p className="no-more-posts">Has llegado al final</p>
            )}
          </div>
        </main>

        {/* Sidebar */}
        <aside className="home-sidebar">
          {/* Categories */}
          <div className="sidebar-card">
            <h3 className="sidebar-title">Categorías</h3>
            <div className="categories-list">
              <button
                type="button"
                className={`category-item ${!activeCategory ? 'active' : ''}`}
                onClick={() => setActiveCategory(null)}
                aria-pressed={!activeCategory}
              >
                <span>✨</span>
                <span>Todas</span>
              </button>
              {CATEGORIES.slice(0, 8).map(category => (
                <button
                  key={category.id}
                  type="button"
                  className={`category-item ${activeCategory === category.id ? 'active' : ''}`}
                  onClick={() => handleCategoryToggle(category.id)}
                  aria-pressed={activeCategory === category.id}
                >
                  <span>{category.icon}</span>
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Trending Posts */}
          <div className="sidebar-card">
            <h3 className="sidebar-title">
              <FiTrendingUp /> Tendencias
            </h3>
            <div className="trending-list">
              {trendingPosts.slice(0, 5).map((post, index) => (
                <Link
                  key={post.id}
                  to={postRoute(post)}
                  state={{ backgroundLocation: location }}
                  className="trending-item"
                >
                  <span className="trending-number">{index + 1}</span>
                  <div className="trending-content">
                    <h4 className="trending-title">{post.title}</h4>
                    <span className="trending-meta">{post.comments ?? post.commentsCount ?? 0} comentarios</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Home;
